import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getSharedResearchersService } from '../services';
import { PERMISSION } from '../components/investigation/share/user_roles';

interface UseResearcherRoleOptions {
    /** UUID of the investigation */
    uuidInvestigation: string;
    /** UUID of the researcher whose role to resolve */
    uuidResearcher?: string;
    /** Whether the hook should fetch (default: true) */
    enabled?: boolean;
}

/**
 * SHER-224 — resolve a researcher's role NAME within an investigation
 * (e.g. "DOCTOR", "NURSE", "ADMIN", or a custom role name).
 *
 * The role is not part of a patient submission's payload, so it is looked up
 * from the investigation's shared-researcher list. Used to label PDF
 * signatures with the uploader's real role instead of a hardcoded
 * "Médecin Prescripteur". Returns null while loading or if the role can't be
 * determined — callers must treat null as "no title".
 *
 * SHER-929 — the shared-researcher list is a SHARE_RESEARCHERS endpoint. For a
 * caller without that permission (interns, surgery, nursing) the hook does not
 * ask and returns null, so the PDF renders without a role line: the output those
 * users already got when the API refused the request, minus the 401.
 */
export const useResearcherRole = ({
    uuidInvestigation,
    uuidResearcher,
    enabled = true,
}: UseResearcherRoleOptions): string | null => {
    const [roleName, setRoleName] = useState<string | null>(null);
    // The signed-in user's permissions are only known for the current investigation.
    const canReadResearchers = useSelector((state: any) => {
        const current = state.investigations?.currentInvestigation;
        return Boolean(current && current.uuid === uuidInvestigation
            && current.permissions?.includes(PERMISSION.SHARE_RESEARCHERS));
    });

    useEffect(() => {
        if (!enabled || !canReadResearchers || !uuidInvestigation || !uuidResearcher) {
            setRoleName(null);
            return;
        }
        let cancelled = false;
        getSharedResearchersService(uuidInvestigation)
            .then((data: any) => {
                if (cancelled) return;
                const list = data?.sharedResearchers || data?.researchers
                    || (Array.isArray(data) ? data : []);
                const match = Array.isArray(list)
                    ? list.find((r: any) => r && r.uuid === uuidResearcher)
                    : null;
                setRoleName(match?.role?.name || null);
            })
            .catch(() => {
                if (!cancelled) setRoleName(null);
            });
        return () => { cancelled = true; };
    }, [uuidInvestigation, uuidResearcher, enabled, canReadResearchers]);

    return roleName;
};
